import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-mono text-base text-white uppercase tracking-wider mb-4 border-b border-[#2A2A2A] pb-3">
        {title}
      </h2>
      <div className="text-[#BEB3A6] font-sans leading-relaxed space-y-3 text-[0.95rem]">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyPage() {
  return (
    <div className="bg-[#111111] min-h-screen">
      <Helmet>
        <title>Privacy Policy – Restricted Deals</title>
        <meta name="description" content="How Restricted Deals collects, uses, and protects your personal data under GDPR." />
        <link rel="canonical" href="https://restricteddeals.com/privacy" />
      </Helmet>
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="eyebrow eyebrow-red mb-4">// Legal</p>
        <h1 className="font-mono text-4xl md:text-5xl text-white uppercase tracking-tight mb-4">
          Privacy Policy
        </h1>
        <p className="text-[#555] font-mono text-xs tracking-widest uppercase mb-14">
          Last updated: May 2026 · Restricted Deals · Netherlands
        </p>

        <Section title="1. Who we are">
          <p>
            Restricted Deals ("we", "us") is operated from the Netherlands and runs a curated
            travel deals newsletter at <strong className="text-white">restricteddeals.com</strong>.
            For privacy enquiries contact{" "}
            <a href="mailto:hello@restricteddeals.com" className="text-[#CC1A1A] hover:underline">
              hello@restricteddeals.com
            </a>.
          </p>
        </Section>

        <Section title="2. Data we collect">
          <p>We collect only what is strictly necessary:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong className="text-white">Email address</strong> — required to deliver deal emails.</li>
            <li><strong className="text-white">First name</strong> — optional, used to personalise the greeting.</li>
            <li>
              <strong className="text-white">Anonymised IP address</strong> — when you click a deal link,
              we log an anonymised IP (last octet masked, e.g. 192.168.1.<strong className="text-white">0</strong>)
              and browser type for aggregate click analytics. This data is not linked to your subscriber
              record and is not used to identify you.
            </li>
          </ul>
          <p className="mt-2">
            We do <strong className="text-white">not</strong> collect payment data, passwords, or
            sensitive personal data. We do <strong className="text-white">not</strong> sell your data.
          </p>
        </Section>

        <Section title="3. Legal basis">
          <p>
            We process your email and name on the basis of <strong className="text-white">consent</strong> (Article
            6(1)(a) GDPR). You give consent by subscribing and confirming your email address via the
            double opt-in link we send. You may withdraw consent at any time by unsubscribing.
          </p>
          <p>
            Anonymised click analytics are processed on the basis of our <strong className="text-white">legitimate
            interest</strong> (Article 6(1)(f) GDPR) in understanding which deals are most useful to our
            subscribers. The last octet of your IP is masked before storage so the data is not personal data
            within the meaning of GDPR.
          </p>
        </Section>

        <Section title="4. How we use your data">
          <ul className="list-disc pl-5 space-y-1">
            <li>Sending deal emails when we spot significant price drops on flights, hotels, cruises, and packages.</li>
            <li>Sending a one-time confirmation email when you subscribe.</li>
          </ul>
          <p className="mt-2">
            We do not use your data for advertising, profiling, or automated decision-making.
          </p>
        </Section>

        <Section title="5. Affiliate link disclosure">
          <p>
            Deal emails and pages contain affiliate links to booking platforms (e.g. airline websites,
            Booking.com, Trip.com). If you click a link and make a purchase, we may earn a small
            commission at no extra cost to you. This never influences which deals we feature.
          </p>
        </Section>

        <Section title="6. Analytics">
          <p>
            We use <strong className="text-white">PostHog</strong> for anonymous site analytics (page
            views, session counts). PostHog is configured with{" "}
            <code className="text-[#CC1A1A] text-sm">person_profiles: "identified_only"</code> —
            no personal profile is created for regular visitors. Analytics only load after you accept
            cookies. You can decline or withdraw consent via our cookie banner or the{" "}
            <Link to="/cookies" className="text-[#CC1A1A] hover:underline">Cookie Policy</Link> page.
          </p>
        </Section>

        <Section title="7. Data processors">
          <p>
            We use the following third-party processors who act on our instructions and are bound
            by Data Processing Agreements (DPAs) or equivalent safeguards:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-2">
            <li>
              <strong className="text-white">Resend</strong> (US) — email delivery. Receives subscriber
              email addresses to dispatch the newsletter. GDPR-compliant; DPA in place.
            </li>
            <li>
              <strong className="text-white">PostHog</strong> (US) — analytics. Receives anonymised
              usage data (page views, session counts) after consent. Operates under the EU–US Data
              Privacy Framework.
            </li>
            <li>
              <strong className="text-white">Railway</strong> (US) — cloud hosting and database. All
              subscriber data is stored on Railway's infrastructure. Railway provides infrastructure
              DPA terms.
            </li>
            <li>
              <strong className="text-white">Cloudflare</strong> (US) — content delivery network and
              DDoS protection. Processes network-level request metadata (IP addresses) as a data
              processor. Cloudflare is certified under the EU–US Data Privacy Framework.
            </li>
            <li>
              <strong className="text-white">Google AdSense</strong> (US) — advertising. Serves ads
              and sets advertising cookies after consent. Google processes data under its own privacy
              policy and is certified under the EU–US Data Privacy Framework.
            </li>
          </ul>
          <p className="mt-2">
            No subscriber personal data is shared with any other third party for marketing or
            commercial purposes.
          </p>
        </Section>

        <Section title="8. International data transfers">
          <p>
            Some of our processors are based in the United States. We rely on the following
            mechanisms to ensure your data is protected when transferred outside the EEA:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>
              <strong className="text-white">EU–US Data Privacy Framework (DPF)</strong> — Cloudflare
              and PostHog are certified under the DPF, which provides an adequacy decision for
              transfers to participating US companies (Commission Decision of 10 July 2023).
            </li>
            <li>
              <strong className="text-white">Standard Contractual Clauses (SCCs)</strong> — for
              processors not covered by the DPF (including Resend and Railway), we rely on the
              European Commission's standard contractual clauses (2021/914) as the transfer mechanism.
            </li>
          </ul>
          <p className="mt-2">
            You may request a copy of the relevant safeguards by emailing{" "}
            <a href="mailto:hello@restricteddeals.com" className="text-[#CC1A1A] hover:underline">
              hello@restricteddeals.com
            </a>.
          </p>
        </Section>

        <Section title="9. Data retention">
          <p>
            We retain your data only for as long as you are subscribed.{" "}
            <strong className="text-white">When you unsubscribe, your subscriber record and associated
            email history are permanently deleted</strong> — we do not retain your email address or name
            after that point. You may also request deletion at any time by emailing{" "}
            <a href="mailto:hello@restricteddeals.com" className="text-[#CC1A1A] hover:underline">
              hello@restricteddeals.com
            </a>.
          </p>
          <p>
            Pending (unconfirmed) subscription requests are purged after 30 days if not confirmed.
            Anonymised click logs are retained for up to 12 months for aggregate analytics.
          </p>
        </Section>

        <Section title="10. Your GDPR rights">
          <p>As a data subject in the EU you have the right to:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong className="text-white">Access</strong> — request a copy of data we hold about you.</li>
            <li><strong className="text-white">Rectification</strong> — ask us to correct inaccurate data.</li>
            <li><strong className="text-white">Erasure</strong> — ask us to delete your data ("right to be forgotten").</li>
            <li><strong className="text-white">Portability</strong> — receive your data in a machine-readable format.</li>
            <li><strong className="text-white">Objection</strong> — object to processing at any time.</li>
            <li><strong className="text-white">Withdraw consent</strong> — unsubscribe via the one-click link in every email.</li>
          </ul>
          <p className="mt-2">
            To exercise any right email{" "}
            <a href="mailto:hello@restricteddeals.com" className="text-[#CC1A1A] hover:underline">
              hello@restricteddeals.com
            </a>. We respond within 30 days.
          </p>
        </Section>

        <Section title="11. Age restriction">
          <p>
            Our newsletter is intended for persons aged <strong className="text-white">16 or older</strong>.
            In accordance with Article 8 GDPR and Dutch law, we do not knowingly collect personal
            data from children under 16. By subscribing you confirm that you meet this age requirement.
            If we become aware that we have collected data from a person under 16, we will delete it
            promptly.
          </p>
        </Section>

        <Section title="12. Data breach notification">
          <p>
            In the event of a personal data breach, we will notify the{" "}
            <strong className="text-white">Autoriteit Persoonsgegevens</strong> within 72 hours of
            becoming aware of the breach, where required by Article 33 GDPR. Where a breach is likely
            to result in a high risk to your rights and freedoms, we will also notify affected
            subscribers without undue delay as required by Article 34 GDPR.
          </p>
        </Section>

        <Section title="13. Supervisory authority">
          <p>
            If you believe we have handled your data unlawfully you may lodge a complaint with the{" "}
            <strong className="text-white">Autoriteit Persoonsgegevens</strong> (Dutch Data Protection Authority) at{" "}
            <a href="https://www.autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="text-[#CC1A1A] hover:underline">
              autoriteitpersoonsgegevens.nl
            </a>.
          </p>
        </Section>

        <Section title="14. Changes to this policy">
          <p>
            We will notify you by email of any material changes to how we process your personal data
            before those changes take effect. For minor clarifications (typos, wording improvements,
            or new processor contact details) we will update this page and revise the date above
            without individual notice.
          </p>
        </Section>

        <div className="hr-dot my-10" />
        <div className="flex gap-6 text-[11px] font-mono tracking-widest uppercase text-[#555]">
          <Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
          <Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
